import React from 'react';
import '@atlaskit/css-reset';
import type { PreloadedQuery } from 'react-relay';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import { SetCustomMark } from '@atlassian/jira-providers-spa-apdex-analytics/src';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import type { TenantContext } from '@atlassian/jira-shared-types';
import { NextGenBacklogSkeleton } from '@atlassian/jira-skeletons';
import BacklogPage from '@atlassian/jira-software-backlog';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import { BacklogSlaTracker } from '@atlassian/jira-software-backlog/src/view/backlog-sla-tracker';
import BoardSizeLimitErrorEmptyState, {
	isBoardSizeLimitError,
} from '@atlassian/jira-software-board-size-limit-error-empty-state';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller';
import type { RouteResourceError } from '../../../../../common/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bindToOnJiraGlobalIssueCreateClose = (callback: any) => {
	// window.require is not available on initial script load.
	// eslint-disable-next-line jira/wrm/no-load-bridge
	loadBridge({
		name: 'jira/util/events',
		wrmKeys: 'wrc!jira.webresources:jira-events',
	}).then((Events) => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		Events?.bind('QuickCreateIssue.sessionComplete', callback);
	});
};

type BacklogAppProps = {
	rapidViewId: number;
	projectKey: string;
	tenantContext: TenantContext;
	data: PrefetchedDataGql | null;
	aggData: aggBacklogDataQuery$data | null;
	projectKeyFromContext: string;
	projectIdFromContext: string;
	projectNameFromContext: string;
	error: RouteResourceError;
	loading: Boolean;
	isCacheHit: boolean;
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null;
	versionFilterRelayQueryRef?: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null;
	getDataFromAGG?: () => Promise<aggBacklogDataQuery$data>;
};

const BacklogApp = ({
	rapidViewId,
	projectKey,
	projectKeyFromContext,
	projectIdFromContext,
	projectNameFromContext,
	tenantContext,
	data,
	aggData,
	error,
	loading,
	isCacheHit,
	customFiltersQueryRef,
	versionFilterRelayQueryRef,
	getDataFromAGG,
}: BacklogAppProps) => {
	const isAppReady = Boolean(data);

	if (isBoardSizeLimitError(error)) {
		return <BoardSizeLimitErrorEmptyState projectKey={projectKey} />;
	}

	if (error && !loading) {
		return <BacklogSlaTracker error={error} />;
	}

	if (!isAppReady) {
		return <NextGenBacklogSkeleton />;
	}

	return (
		<>
			<SetCustomMark name="configApiReady" />
			{/* @ts-expect-error - TS2739 - Type '{ isCacheHit: boolean; bindToOnJiraGlobalIssueCreateClose: (callback: any) => void; projectKey: string; projectId: number; rapidViewId: string; locale: Locale; baseUrl: string; xsrfToken: string; ... 5 more ...; customFiltersQueryRef: PreloadedQuery<...> | ... 1 more ... | undefined; }' is missing the following properties from type 'RouteContext': location, query, route, match, action */}
			<BacklogPage
				isCacheHit={isCacheHit}
				bindToOnJiraGlobalIssueCreateClose={bindToOnJiraGlobalIssueCreateClose}
				// Route match data that could be read inside of the app
				projectKey={projectKeyFromContext}
				projectId={Number(projectIdFromContext)}
				projectName={projectNameFromContext}
				rapidViewId={String(rapidViewId)}
				// Tenant context data that could be read inside of the app
				locale={tenantContext.locale}
				baseUrl={tenantContext.baseUrl}
				xsrfToken={tenantContext.xsrfToken ?? ''}
				cloudId={tenantContext.cloudId ?? ''}
				atlassianAccountId={tenantContext.atlassianAccountId ?? null}
				prefetchedData={data}
				prefetchedAggData={aggData}
				isServer={__SERVER__}
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
				getDataFromAGG={getDataFromAGG}
			/>
			<SpaStatePageReady />
		</>
	);
};

export default BacklogApp;
